import { GL, GLSL_COMMON, Program } from '../gl';

// Dynamic point sprites in the ENU frame (satellites and other moving lights).
const VS = /* glsl */ `#version 300 es
precision highp float;
${GLSL_COMMON}
layout(location = 0) in vec3 a_enu;
layout(location = 1) in float a_size;   // device px
layout(location = 2) in vec4 a_color;   // rgb, a = intensity
out vec4 v_color;
out float v_size;
void main() {
  gl_PointSize = 0.0;
  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  v_color = a_color;
  v_size = a_size;
  if (a_color.a <= 0.001) return;
  vec3 p = projectCam(u_enuToCam * refractEnu(a_enu));
  if (p.z < 0.5) return;
  gl_PointSize = clamp(a_size, 1.0, 96.0);
  gl_Position = vec4(p.xy, 0.0, 1.0);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
in vec4 v_color;
in float v_size;
out vec4 o;
void main() {
  vec2 q = gl_PointCoord * 2.0 - 1.0;
  float r2 = dot(q, q);
  if (r2 > 1.0) discard;
  // Bright core plus a faint ring, so moving objects read differently from stars.
  float r = sqrt(r2);
  float core = exp(-r2 * 30.0);
  float ring = exp(-pow((r - 0.62) / 0.09, 2.0)) * 0.22;
  float halo = exp(-r2 * 7.0) * 0.18;
  float i = (core * 1.2 + halo) * v_color.a;
  vec3 c = mix(v_color.rgb, vec3(1.0), core * 0.6) * i + v_color.rgb * ring * min(v_color.a, 1.0);
  o = vec4(c, i + ring * 0.5);
}`;

export class PointLayer {
  private prog: Program;
  private vao: WebGLVertexArrayObject;
  private buf: WebGLBuffer;
  count = 0;
  private cap = 0;

  constructor(private gl: GL) {
    this.prog = new Program(gl, VS, FS, 'points');
    this.vao = gl.createVertexArray()!;
    this.buf = gl.createBuffer()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    const stride = 8 * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 1, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, stride, 16);
    gl.bindVertexArray(null);
  }

  /** data: [ex, ey, ez, sizePx, r, g, b, a] per point */
  setData(data: Float32Array, n: number) {
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buf);
    if (data.byteLength > this.cap) {
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.DYNAMIC_DRAW);
      this.cap = data.byteLength;
    } else gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, n * 8);
    this.count = n;
  }

  draw(setCommon: (p: Program) => void) {
    if (!this.count) return;
    const gl = this.gl;
    const p = this.prog.use();
    setCommon(p);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.POINTS, 0, this.count);
    gl.bindVertexArray(null);
  }
}
